import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, of } from "rxjs";
import {
  FeatureFlags,
  FeatureFlagKey,
  DEFAULT_FEATURE_FLAGS,
  ALL_FEATURE_FLAG_KEYS,
  FeatureFlagsApiResponse,
} from "./feature-flags.model";
import { HttpClient } from "../../shared/services/http/http-client.service";

const STORAGE_KEY = "agilx_feature_flags";

@Injectable({
  providedIn: "root",
})
export class FeatureFlagsService {
  private featureFlagsSubject = new BehaviorSubject<FeatureFlags>(this.loadFromStorage());
  public featureFlags$: Observable<FeatureFlags> = this.featureFlagsSubject.asObservable();

  constructor(private http: HttpClient) {
    // Lazy-load updates from the server in the background (non-blocking)
    this.refreshFlagsInBackground();
  }

  getFlags(): FeatureFlags {
    return this.featureFlagsSubject.value;
  }

  isEnabled(feature: FeatureFlagKey): boolean {
    return this.featureFlagsSubject.value[feature] ?? false;
  }

  toggleFeature(feature: FeatureFlagKey): void {
    const currentFlags = this.featureFlagsSubject.value;
    const updatedFlags: FeatureFlags = {
      ...currentFlags,
      [feature]: !currentFlags[feature],
    };
    this.updateFlags(updatedFlags);
  }

  setFeature(feature: FeatureFlagKey, enabled: boolean): void {
    const currentFlags = this.featureFlagsSubject.value;
    if (currentFlags[feature] !== enabled) {
      const updatedFlags: FeatureFlags = {
        ...currentFlags,
        [feature]: enabled,
      };
      this.updateFlags(updatedFlags);
    }
  }

  enableAll(): void {
    const allEnabled = { ...DEFAULT_FEATURE_FLAGS };
    ALL_FEATURE_FLAG_KEYS.forEach((key) => {
      allEnabled[key] = true;
    });
    this.updateFlags(allEnabled);
  }

  disableAll(): void {
    this.updateFlags({ ...DEFAULT_FEATURE_FLAGS });
  }

  reset(): void {
    this.disableAll();
  }

  private updateFlags(flags: FeatureFlags): void {
    // Update local cache immediately for responsive UI
    this.saveToStorage(flags);
    this.featureFlagsSubject.next(flags);

    // Convert to API format and persist to server in the background
    const apiPayload = this.toApiFormat(flags);
    this.http
      .put("api/featureflags", apiPayload)
      .pipe(
        catchError((error) => {
          console.warn("Failed to save feature flags to server:", error);
          return of(null);
        })
      )
      .subscribe();
  }

  private loadFromStorage(): FeatureFlags {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new flags
        return { ...DEFAULT_FEATURE_FLAGS, ...parsed };
      }
    } catch (error) {
      console.warn("Failed to load feature flags from storage:", error);
    }
    return { ...DEFAULT_FEATURE_FLAGS };
  }

  private saveToStorage(flags: FeatureFlags): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    } catch (error) {
      console.warn("Failed to save feature flags to storage:", error);
    }
  }

  private refreshFlagsInBackground(): void {
    // Use setTimeout to ensure this doesn't block the main thread
    setTimeout(() => {
      this.http
        .get<FeatureFlagsApiResponse>("api/featureflags")
        .pipe(
          catchError((error) => {
            console.warn("Failed to fetch feature flags from server:", error);
            return of(null);
          })
        )
        .subscribe((response) => {
          if (response?.flags) {
            const mergedFlags = this.fromApiFormat(response.flags);
            this.saveToStorage(mergedFlags);
            this.featureFlagsSubject.next(mergedFlags);
          }
        });
    }, 0);
  }

  refresh(): void {
    this.http
      .get<FeatureFlagsApiResponse>("api/featureflags")
      .pipe(
        catchError((error) => {
          console.warn("Failed to fetch feature flags from server:", error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response?.flags) {
          const mergedFlags = this.fromApiFormat(response.flags);
          this.saveToStorage(mergedFlags);
          this.featureFlagsSubject.next(mergedFlags);
        }
      });
  }

  /**
   * Convert API response format to client FeatureFlags format
   * Server uses PascalCase (Clients), client uses camelCase (clients)
   */
  private fromApiFormat(apiFlags: Record<string, boolean>): FeatureFlags {
    const result = { ...DEFAULT_FEATURE_FLAGS };

    // Map server PascalCase keys to client camelCase keys
    const keyMapping: Record<string, FeatureFlagKey> = {
      Clients: "clients",
      Drivers: "drivers",
      Tractors: "tractors",
      Trailers: "trailers",
      Locations: "locations",
      BillTos: "billTos",
      DispatchFinder: "dispatchFinder",
      DispatchDriverAssignments: "dispatchDriverAssignments",
      DispatchTrips: "dispatchTrips",
      NewReports: "newReports",
      UseNewDriverPages: "useNewDriverPages",
      UseNewClientPages: "useNewClientPages",
      UseNewTractorPages: "useNewTractorPages",
      UseNewTrailerPages: "useNewTrailerPages",
      EmailFeatures: "emailFeatures",
    };

    for (const [serverKey, clientKey] of Object.entries(keyMapping)) {
      if (serverKey in apiFlags) {
        result[clientKey] = apiFlags[serverKey];
      }
    }

    return result;
  }

  /**
   * Convert client FeatureFlags format to API format
   * Server expects PascalCase (Clients), client uses camelCase (clients)
   */
  private toApiFormat(flags: FeatureFlags): { flags: Record<string, boolean> } {
    // Map client camelCase keys to server PascalCase keys
    const keyMapping: Record<FeatureFlagKey, string> = {
      clients: "Clients",
      drivers: "Drivers",
      tractors: "Tractors",
      trailers: "Trailers",
      locations: "Locations",
      billTos: "BillTos",
      dispatchFinder: "DispatchFinder",
      dispatchDriverAssignments: "DispatchDriverAssignments",
      dispatchTrips: "DispatchTrips",
      newReports: "NewReports",
      useNewDriverPages: "UseNewDriverPages",
      useNewClientPages: "UseNewClientPages",
      useNewTractorPages: "UseNewTractorPages",
      useNewTrailerPages: "UseNewTrailerPages",
      emailFeatures: "EmailFeatures",
    };

    const apiFlags: Record<string, boolean> = {};
    for (const [clientKey, serverKey] of Object.entries(keyMapping)) {
      apiFlags[serverKey] = flags[clientKey as FeatureFlagKey];
    }

    return { flags: apiFlags };
  }
}
